import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useParams } from "react-router";

import { Warehouse } from "@secondcloset/types";

import { notification, Radio, Space, Spin, Typography } from "antd";
import { isEmpty } from "lodash-es";

import { PackagingLevel, Product as TProduct } from "interfaces/products";

import ActivityHistory, { ActivityHistoryType } from "../../../components/ActivityHistory";
import { ResourceError } from "../../../components/ResourceError";
import resize from "../../../helperFunctions/FulfillmentProductCreatePage/resize";
import { useProductDetails } from "../../../hooks/api/fulfillment/products";
import useProductCreateFlow from "../../../hooks/reduxContainer/fulfillment/productCreateFlow";
import FulfillmentBackButton from "../FulfillmentBackButton";
import BaseProducts from "../FulfillmentProductCreatePage/BaseProducts";
import CustomsInfo from "../FulfillmentProductCreatePage/CustomsInfo";
import PackingDetails from "../FulfillmentProductCreatePage/PackingDetails";
import ProductDetails from "../FulfillmentProductCreatePage/ProductDetails";
import ProductInfo from "../FulfillmentProductCreatePage/ProductInfo";

import AssociatedVirtualKits from "./AssociatedVirtualKits";
import { PageWrapper, InnerWrapper } from "./FulfillmentProductDetailsPage.styles";
import { fetchResourceErrorsIndex } from "../../../api/fulfillment/resourceError";
import { Flex } from "css/styles";
import { ProductInventoryTable } from "components/ProductInventoryTable";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlagQuery";

type Stock = Warehouse.Stock;

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 5;
const LOG_SEGMENT_LIST = [
  { value: "merchant", label: "Product Logs" },
  { value: "warehouse", label: "Warehouse Activity" },
];

const FulfillmentProductDetailsPage: React.FC = () => {
  const [imsStocks, setImsStocks] = useState<Stock[]>([]);
  const { isEditing, product, dispatchSetIsEditing, dispatchSetProduct, dispatchResetProduct } = useProductCreateFlow();
  const {
    product_type: productType,
    virtual_kits: virtualKits,
    // base_products,
    // required_parts,
    name,
    image,
    image_url,
    product_type,
    active,
    description,
    scid,
    sku,
    upc,
    length,
    width,
    height,
    weight,
    weight_unit,
    tracks_lot_numbers,
    dangerous,
    fragile,
    contains_battery,
    liquid,
    tracks_expiry_date,
    days_before_expiry_to_block_fulfillment,
    packaging_levels,
    hs_tariff_code,
    customs_description,
    cost,
    wholesale_price,
    retail_price,
    platform,
    insurance_required,
    insurance_value,
    under_conversion,
    is_service,
  } = product;
  const { productDetails, fetchProduct } = useProductDetails();
  const params: { product_id: string; organization_id: string } = useParams();
  const { product_id: productID, organization_id: organizationID } = params;
  const [segment, setSegment] = useState(LOG_SEGMENT_LIST[0].value);
  const [page, setPage] = useState({ current: DEFAULT_PAGE, size: DEFAULT_PAGE_SIZE });

  const showPackingDetails = useFeatureFlagQuery("show-product-packing-details", { org_id: organizationID });

  const productResourceError = useQuery(
    [`productResourceError`, product.id, page],
    () =>
      fetchResourceErrorsIndex({
        resource_id: product.id,
        resource_type: "product",
        page: page.current,
        per_page: page.size,
      }),
    { enabled: !!product.id }
  );

  const formatPackagingLevels = (packaging_levels: PackagingLevel[]) => {
    const levels = ["inner_pack", "master_pack", "pallet"] as PackagingLevel["level"][];

    // If there are multiple packaging levels, find the
    const packagingLevels = levels.map((level) => {
      const packagingLevel = packaging_levels?.find((packagingLevel) => packagingLevel.level === level);
      if (packagingLevel) return { ...packagingLevel, active: true };
      return {
        active: false,
        level,
        base_product_quantity: 0,
        sku: "",
        description: "",
        weight_value: 0,
        weight_unit: "lb",
        length_value: 0,
        length_unit: "in",
        height_value: 0,
        height_unit: "in",
        width_value: 0,
        width_unit: "in",
      };
    });

    return packagingLevels;
  };

  const getEditableProduct = (productDetails: any) => {
    const {
      id,
      active,
      base_products,
      dangerous,
      description,
      fragile,
      height,
      hs_tariff_code,
      images,
      length,
      name,
      product_type,
      scid,
      sku,
      upc,
      // stocks,
      // value,
      virtual_kits,
      weight,
      weight_unit,
      width,
      contains_battery,
      cost,
      created_at,
      customs_description,
      deleted_at,
      digital,
      liquid,
      tracks_expiry_date,
      days_before_expiry_to_block_fulfillment,
      organization_id,
      packaging_criteria,
      packaging_levels,
      required_parts,
      retail_price,
      wholesale_price,
      tracks_lot_numbers,
      platform,
      insurance_required,
      insurance_value,
      under_conversion,
      manufacturing_country_origin,
      is_service,
    } = productDetails;

    const packagingLevels = formatPackagingLevels(packaging_levels);

    return {
      ...product,
      id,
      active,
      base_products: base_products || [],
      dangerous,
      description,
      fragile,
      height,
      hs_tariff_code,
      // id,
      image: null,
      image_url: images[0]?.url ? images[0].url : null,
      length,
      name,
      product_type,
      scid,
      sku,
      upc,
      // stocks,
      // value,
      virtual_kits,
      weight,
      weight_unit,
      width,
      contains_battery,
      cost,
      created_at,
      customs_description,
      deleted_at,
      digital,
      liquid,
      tracks_expiry_date,
      days_before_expiry_to_block_fulfillment,
      organization_id,
      packaging_criteria,
      packaging_levels: packagingLevels,
      platform,
      required_parts: required_parts || {},
      retail_price,
      wholesale_price,
      tracks_lot_numbers,
      insurance_required,
      insurance_value,
      under_conversion,
      manufacturing_country_origin,
      is_service,
    } as TProduct;
  };

  const setup = async () => {
    resize();
    dispatchResetProduct();
    try {
      const product = await fetchProduct(productID);
      const formattedProduct = getEditableProduct(product);
      dispatchSetProduct(formattedProduct);
      dispatchSetIsEditing(false);
    } catch (e) {
      const error = e as Error | string;
      notification.error({
        message: "Error",
        description: typeof error === "string" ? error : error.message,
      });
    }
  };

  useEffect(
    () => {
      setup();
    },
    // eslint-disable-next-line
    [productID]
  );

  const setProduct = () => {
    const product = getEditableProduct(productDetails.data);
    dispatchSetProduct(product);
  };

  const renderActivityHistory = () => {
    return (
      <ActivityHistory
        title={
          <Space align="start">
            <Typography.Title level={4}>ACTIVITY HISTORY</Typography.Title>
            <Radio.Group defaultValue={segment} size="small" onChange={(e) => setSegment(e.target.value)}>
              {LOG_SEGMENT_LIST.map((i) => (
                <Radio.Button key={i.value} value={i.value}>
                  {i.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Space>
        }
        id={productID}
        orgID={organizationID}
        type={segment === "merchant" ? ActivityHistoryType.PRODUCT : ActivityHistoryType.WAREHOUSE_PRODUCT}
        enableFetch={!isEmpty(productDetails.data)}
        product={{ sku: product.sku }}
      />
    );
  };

  const renderProductInventoryTable = () => {
    if (!organizationID) return;
    return <ProductInventoryTable productId={product.id} orgId={organizationID} />;
  };

  const renderBody = () => {
    if (is_service) {
      return renderActivityHistory();
    }

    if (packaging_levels && productType === "base") {
      return (
        <Flex direction="column" gap="1em" align="stretch">
          {renderProductInventoryTable()}
          <ProductDetails
            isEditing={isEditing}
            length={length}
            width={width}
            height={height}
            weight={weight}
            weight_unit={weight_unit}
            tracks_lot_numbers={tracks_lot_numbers}
            dangerous={dangerous}
            fragile={fragile}
            contains_battery={contains_battery}
            liquid={liquid}
            tracksExpiryDate={tracks_expiry_date}
            daysBeforeExpiryToBlockFulfillment={days_before_expiry_to_block_fulfillment}
            platform={platform}
          />
          {showPackingDetails.data?.state && <PackingDetails packaging_levels={packaging_levels} />}
          <CustomsInfo
            hs_tariff_code={hs_tariff_code}
            customs_description={customs_description}
            cost={cost}
            wholesale_price={wholesale_price}
            retail_price={retail_price}
            manufacturingCountry={product.manufacturing_country_origin}
          />
          {/* <PackageRequirements /> */}
          <AssociatedVirtualKits virtualKits={virtualKits} loading={productDetails.loading} />
          {!!productResourceError.data?.data.length && (
            <ResourceError
              title="PRODUCT ERRORS"
              resourceError={productResourceError.data}
              isLoading={productResourceError.isLoading}
              organizationID={organizationID}
              refetch={productResourceError.refetch}
              pagination={{
                current: page.current,
                pageSize: page.size,
                onChange: (current, size) => {
                  setPage((page) => ({ ...page, current, size: size ?? page.size }));
                },
              }}
            />
          )}
          {renderActivityHistory()}
        </Flex>
      );
    }
    if (productType === "kit") {
      return (
        <>
          {renderProductInventoryTable()}
          <BaseProducts />
          {!!productResourceError.data?.data.length && (
            <ResourceError
              title="PRODUCT ERRORS"
              resourceError={productResourceError.data}
              isLoading={productResourceError.isLoading}
              organizationID={organizationID}
              refetch={productResourceError.refetch}
              pagination={{
                current: page.current,
                pageSize: page.size,
                onChange: (current, size) => {
                  setPage((page) => ({ ...page, current, size: size ?? page.size }));
                },
              }}
            />
          )}
        </>
      );
    }
  };

  return (
    <PageWrapper>
      <InnerWrapper>
        <FulfillmentBackButton urlPath={`/organizations/${organizationID}`} label="Back to Organization" />
        <Spin spinning={productDetails.loading}>
          <ProductInfo
            setProduct={setProduct}
            onUpdateProduct={setup}
            isEditing={isEditing}
            name={name}
            image={image}
            image_url={image_url}
            product_type={product_type}
            active={active}
            description={description}
            scid={scid}
            sku={sku}
            upc={upc}
            platform={platform as any}
            isService={is_service}
            insuranceRequired={insurance_required}
            insuranceValue={insurance_value}
            cost={cost}
            under_conversion={under_conversion}
          />
          {renderBody()}
        </Spin>
      </InnerWrapper>
    </PageWrapper>
  );
};

export default FulfillmentProductDetailsPage;
